import { useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { selectWysiwigEditorEnabled } from 'core/userContext/selectors';
import { HtmlEditor, MessageMedium } from 'modules/graphql/types';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { body1 } from 'ui/theme';
import { prefixWithUserProject } from 'utils/indexPageUtils';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import { ModalType, useModalContext } from 'modules/templateEditor/context';
import { displayContent } from 'modules/templateEditor/common';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { ContentEditorSelector } from './ContentEditorSelector';
import { useSelectContentEditorContext } from './contexts';

export const SELECT_CONTENT_EDITOR_MODAL_TEST_ID = 'content-editor-selector-modal';

const Description = styled.span`
  ${body1}
  text-align: left;
`;

export const SelectContentEditorModal = () => {
  const { medium } = useTemplateQueryContext();

  const [selectedEditor, setSelectedEditor] = useLocalStorage(
    prefixWithUserProject('template-edit-default-content-editor'),
    '' as HtmlEditor,
  );

  const { onSelectEditor } = useSelectContentEditorContext();
  const { closeModal } = useModalContext();

  const isWysiwygEditorMode = useSelector(selectWysiwigEditorEnabled);
  const wysiwygEditorEnabled = isWysiwygEditorMode || medium === MessageMedium.InApp;

  const handleCloseModal = () => {
    closeModal(ModalType.SelectContentEditorModals);
  };

  const shouldDisplayContinue =
    medium === MessageMedium.Email && // Template selector is only shown for Email
    selectedEditor === HtmlEditor.Bee;

  const mainButtonLabel = shouldDisplayContinue ? displayContent.btnNext : displayContent.btnSelect;
  const mainButtonIsDisabled = !selectedEditor;

  const { getTemplates } = useSelectContentEditorContext();

  useEffect(() => {
    getTemplates();
  }, []);

  return (
    <Modal
      data-test={SELECT_CONTENT_EDITOR_MODAL_TEST_ID}
      id={SELECT_CONTENT_EDITOR_MODAL_TEST_ID}
      isModalOpen={true}
      onClose={handleCloseModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{displayContent.headerSelectEditorType}</ModalHeader>
      <ModalBody>
        <Description>
          {wysiwygEditorEnabled
            ? displayContent.strSelectEditorInfo
            : displayContent.strSelectEditorInfoDisabled}
        </Description>
        <ContentEditorSelector
          setSelectedEditor={setSelectedEditor}
          selectedEditor={selectedEditor}
        />
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={() => onSelectEditor(selectedEditor)}
          isDisabled={mainButtonIsDisabled}
          size={BUTTON_SIZES.LARGE}
        >
          <span>{mainButtonLabel}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
